import { useCallback } from "react";

export interface UseScrollToTopOptions {
  behavior?: ScrollBehavior;
  block?: ScrollLogicalPosition;
}

/**
 * Custom hook to handle scroll-to-top functionality for paginated content.
 * This hook finds the correct scrollable container in nested layouts and
 * scrolls smoothly to the top when pagination changes.
 */
export function useScrollToTop(options: UseScrollToTopOptions = {}) {
  const { behavior = "smooth", block = "start" } = options;

  const scrollToTop = useCallback(() => {
    // Find the main content scrollable containers from LazoAppContainer
    const contentContainer = document.querySelector('[data-testid="content-container"]');
    const contentStack = document.querySelector('[data-testid="content-stack"]');

    // Try ContentStack first (inner container), then ContentContainer
    const containers = [contentStack, contentContainer].filter(Boolean);

    for (const container of containers) {
      if (container && container.scrollHeight > container.clientHeight) {
        try {
          if ("scrollTo" in container && typeof container.scrollTo === "function") {
            container.scrollTo({
              top: 0,
              behavior,
            });
            return;
          }

          if ("scrollTop" in container) {
            container.scrollTop = 0;
            return;
          }
        } catch (error) {
          console.warn("Failed to scroll container:", error);
          continue;
        }
      }
    }

    // Fallback to window scrolling
    if (typeof window !== "undefined" && window.scrollTo) {
      window.scrollTo({
        top: 0,
        behavior,
      });
    }
  }, [behavior]);

  return { scrollToTop };
}
